/**
 * Cores de marca por perfil — alinhadas aos ambientes do desktop
 * (`ROLE_DEFINITIONS` / CockpitDashboardBackground): accent saturado + gradiente escuro.
 * Overrides via Customização Visual (estilos.brandByProfile / brandGradientByProfile).
 */

export type BrandProfileKey =
  | 'PREFEITO'
  | 'DIRETOR'
  | 'SECRETARIA'
  | 'COORDENADOR'
  | 'PROFESSOR'
  | 'RESPONSAVEL'
  | 'ALUNO'
  | 'MASTER'
  | 'ESTADUAL'
  | 'REDE';

/** Accent (ícones desktop, destaques) — primary dos ambientes do cockpit */
export const DEFAULT_BRAND_BY_PROFILE: Record<BrandProfileKey, string> = {
  PREFEITO: '#d8ad54',
  DIRETOR: '#5971ff',
  SECRETARIA: '#ff7785',
  /** Próximo da Secretaria (família rosa), mas fúcsia/violeta — distinto */
  COORDENADOR: '#e879f9',
  PROFESSOR: '#58e19d',
  RESPONSAVEL: '#ff66a9',
  ALUNO: '#ff8c42',
  MASTER: '#9b6cff',
  ESTADUAL: '#3de5ff',
  REDE: '#8b5cf6',
};

/** Secondary do gradiente de ação (botões / glow) */
export const DEFAULT_BRAND_SECONDARY_BY_PROFILE: Record<BrandProfileKey, string> = {
  PREFEITO: '#c78238',
  DIRETOR: '#3ddaff',
  SECRETARIA: '#ffbf5b',
  COORDENADOR: '#f9a8d4',
  PROFESSOR: '#49c3ff',
  RESPONSAVEL: '#ffab7a',
  ALUNO: '#ff6b9d',
  MASTER: '#3de5ff',
  ESTADUAL: '#5971ff',
  REDE: '#a78bfa',
};

/** Fundo da sidebar = atmosfera do splash do desktop (escuro + profundidade) */
export const DEFAULT_BRAND_GRADIENT_BY_PROFILE: Record<BrandProfileKey, string> = {
  PREFEITO: 'linear-gradient(165deg, #1a0f08 0%, #3d2510 48%, #6b4420 100%)',
  DIRETOR: 'linear-gradient(165deg, #07152f 0%, #122c67 48%, #2357a8 100%)',
  SECRETARIA: 'linear-gradient(165deg, #2d0c1c 0%, #612139 48%, #9a4936 100%)',
  COORDENADOR: 'linear-gradient(165deg, #1a0820 0%, #4a1858 48%, #8b2d6b 100%)',
  PROFESSOR: 'linear-gradient(165deg, #041f12 0%, #0a4028 48%, #127040 100%)',
  RESPONSAVEL: 'linear-gradient(165deg, #2c0b28 0%, #61204e 48%, #963f61 100%)',
  ALUNO: 'linear-gradient(165deg, #1a0c08 0%, #3d1a20 48%, #6b2f45 100%)',
  MASTER: 'linear-gradient(165deg, #0b0928 0%, #21145b 48%, #3d2a91 100%)',
  ESTADUAL: 'linear-gradient(165deg, #041018 0%, #0a2a40 48%, #12506b 100%)',
  REDE: 'linear-gradient(165deg, #0d0820 0%, #1e1050 48%, #3b2085 100%)',
};

export const BRAND_PROFILE_LABELS: Record<BrandProfileKey, string> = {
  PREFEITO: 'Prefeito / Município',
  DIRETOR: 'Diretor / Painel Escola',
  SECRETARIA: 'Secretaria / Painel Equipe',
  COORDENADOR: 'Coordenador',
  PROFESSOR: 'Professor',
  RESPONSAVEL: 'Responsável',
  ALUNO: 'Aluno',
  MASTER: 'Master',
  ESTADUAL: 'Estadual',
  REDE: 'Gestor de Rede',
};

/** CSS custom property name for a profile key */
export function brandCssVar(key: BrandProfileKey): string {
  return `--brand-${key.toLowerCase()}`;
}

export function brandGradientCssVar(key: BrandProfileKey): string {
  return `--brand-${key.toLowerCase()}-gradient`;
}

export function parseHexColor(hex?: string | null): { r: number; g: number; b: number } | null {
  if (!hex) return null;
  const raw = hex.trim().replace('#', '');
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

/** Relative luminance 0–1 (sRGB). */
export function hexLuminance(hex: string): number {
  const rgb = parseHexColor(hex);
  if (!rgb) return 0;
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(rgb.r) + 0.7152 * lin(rgb.g) + 0.0722 * lin(rgb.b);
}

function firstHexInCss(value?: string | null): string | null {
  if (!value) return null;
  const m = value.match(/#([0-9a-fA-F]{3,8})\b/);
  return m ? normalizeBrandHex(`#${m[1]}`) : null;
}

/** Texto da sidebar: amostra o fundo (gradiente ou sólido). */
export function resolveSidebarTextColor(bgHex: string, gradient?: string | null): string {
  const sample = firstHexInCss(gradient) || bgHex;
  return hexLuminance(sample) > 0.55 ? '#0f172a' : '#ffffff';
}

export function normalizeBrandHex(raw?: string | null, fallback?: string): string {
  const fb = fallback || '#64748b';
  if (!raw || typeof raw !== 'string') return fb;
  const t = raw.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(t)) return t.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(t)) {
    const r = t.slice(1);
    return `#${r[0]}${r[0]}${r[1]}${r[1]}${r[2]}${r[2]}`.toLowerCase();
  }
  if (/^#[0-9a-fA-F]{8}$/.test(t)) return `#${t.slice(1, 7)}`.toLowerCase();
  return fb;
}

export function mergeBrandByProfile(
  ...layers: Array<Partial<Record<BrandProfileKey, string>> | null | undefined>
): Record<BrandProfileKey, string> {
  const out = { ...DEFAULT_BRAND_BY_PROFILE };
  for (const layer of layers) {
    if (!layer) continue;
    for (const key of Object.keys(DEFAULT_BRAND_BY_PROFILE) as BrandProfileKey[]) {
      if (layer[key]) {
        out[key] = normalizeBrandHex(layer[key], out[key]);
      }
    }
  }
  return out;
}

export function mergeBrandGradientByProfile(
  ...layers: Array<Partial<Record<BrandProfileKey, string>> | null | undefined>
): Record<BrandProfileKey, string> {
  const out = { ...DEFAULT_BRAND_GRADIENT_BY_PROFILE };
  for (const layer of layers) {
    if (!layer) continue;
    for (const key of Object.keys(DEFAULT_BRAND_GRADIENT_BY_PROFILE) as BrandProfileKey[]) {
      const v = layer[key];
      if (v && typeof v === 'string' && v.includes('gradient')) {
        out[key] = v.trim();
      }
    }
  }
  return out;
}

/** Gradiente de ação (botão) a partir de primary/secondary. */
export function buildAccentGradient(primary: string, secondary: string): string {
  return `linear-gradient(135deg, ${normalizeBrandHex(primary)}e6 0%, ${normalizeBrandHex(secondary)}cc 100%)`;
}

/** Lê cor efetiva: CSS var (se aplicada) → window cache → default. */
export function getBrandColor(key: BrandProfileKey): string {
  const fallback = DEFAULT_BRAND_BY_PROFILE[key];
  if (typeof window === 'undefined') return fallback;

  const cached = (window as any).__ac_brand_by_profile as
    | Partial<Record<BrandProfileKey, string>>
    | undefined;
  if (cached?.[key]) return normalizeBrandHex(cached[key], fallback);

  try {
    const cssVal = getComputedStyle(document.documentElement)
      .getPropertyValue(brandCssVar(key))
      .trim();
    if (cssVal) return normalizeBrandHex(cssVal, fallback);
  } catch {
    /* ignore */
  }
  return fallback;
}

export function getBrandGradient(key: BrandProfileKey): string {
  const fallback = DEFAULT_BRAND_GRADIENT_BY_PROFILE[key];
  if (typeof window === 'undefined') return fallback;

  const cached = (window as any).__ac_brand_gradient_by_profile as
    | Partial<Record<BrandProfileKey, string>>
    | undefined;
  if (cached?.[key] && cached[key]!.includes('gradient')) return cached[key]!;

  try {
    const cssVal = getComputedStyle(document.documentElement)
      .getPropertyValue(brandGradientCssVar(key))
      .trim();
    if (cssVal && cssVal.includes('gradient')) return cssVal;
  } catch {
    /* ignore */
  }
  return fallback;
}

export function applyBrandByProfileToDom(
  brandByProfile?: Partial<Record<BrandProfileKey, string>> | null,
  brandGradientByProfile?: Partial<Record<BrandProfileKey, string>> | null,
) {
  const merged = mergeBrandByProfile(brandByProfile);
  const gradients = mergeBrandGradientByProfile(brandGradientByProfile);
  (window as any).__ac_brand_by_profile = merged;
  (window as any).__ac_brand_gradient_by_profile = gradients;

  let styleTag = document.getElementById('dynamic-brand-vars') as HTMLStyleElement | null;
  if (!styleTag) {
    styleTag = document.createElement('style');
    styleTag.id = 'dynamic-brand-vars';
    document.head.appendChild(styleTag);
  }

  const lines = (Object.keys(merged) as BrandProfileKey[])
    .map(
      (k) =>
        `  ${brandCssVar(k)}: ${merged[k]} !important;\n` +
        `  ${brandGradientCssVar(k)}: ${gradients[k]} !important;`,
    )
    .join('\n');
  styleTag.innerHTML = `:root {\n${lines}\n}\n`;
}
